/**
 * api/tryOn.js
 *
 * ⚠️ آدرس‌ها و نام فیلدها فرضی‌اند و باید با بک‌اند خودت یکی شوند:
 *
 * ── کد تایید (OTP) ──
 *   POST  {API}/api/pro-majazi/stores/:storeId/try-on/otp/send/
 *         body: JSON → { customer_mobile }
 *   POST  {API}/api/pro-majazi/stores/:storeId/try-on/otp/verify/
 *         body: JSON → { customer_mobile, code }
 *         پاسخ: { "token": "..." }
 *
 * ── ساخت تصویر ──
 *   POST  {API}/api/pro-majazi/stores/:storeId/products/:productId/try-on/
 *         body: multipart/form-data → person_image, customer_name, customer_mobile,
 *               otp_token, color_id?, product_image_url?
 *   پاسخ می‌تواند یکی از این دو حالت باشد:
 *     ۱) مستقیم:   { "result": "/media/try-on/abc.jpg" }
 *     ۲) ناهمگام:  { "task_id": "123" }   ← بعدش polling روی:
 *   GET   {API}/api/pro-majazi/stores/:storeId/products/:productId/try-on/:taskId/
 *         → { "status": "pending" | "done" | "failed", "result": "...", "detail": "..." }
 *
 * بک‌اند باید otp_token را در درخواست ساخت تصویر بررسی کند؛
 * وگرنه مرحلهٔ OTP در فرانت قابل دور زدن است.
 *
 * اگر برای احراز هویت از axios یا هدر Authorization استفاده می‌کنی،
 * فقط تابع request را عوض کن (بقیهٔ فایل دست نمی‌خورد).
 */
import { API_BASE_URL } from "./http";

const POLL_INTERVAL_MS = 2000;
const TIMEOUT_MS = 120000;

const storeBase = (storeId) =>
  `${API_BASE_URL}/api/pro-majazi/stores/${storeId}`;

const base = (storeId, productId) =>
  `${storeBase(storeId)}/products/${productId}/try-on/`;

async function request(url, options = {}) {
  const res = await fetch(url, { credentials: "include", ...options });

  let data = null;
  try {
    data = await res.json();
  } catch {
    /* پاسخ JSON نبود */
  }

  if (!res.ok) {
    throw new Error(
      data?.detail ||
        data?.message ||
        "خطا در ارتباط با سرور. دوباره تلاش کنید.",
    );
  }
  return data || {};
}

const postJson = (url, body, signal) =>
  request(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

/* ============================== OTP ============================== */

/** ارسال کد تایید پیامکی به موبایل کاربر */
export function sendTryOnOtp({ storeId, phone, signal }) {
  return postJson(
    `${storeBase(storeId)}/try-on/otp/send/`,
    { customer_mobile: phone },
    signal,
  );
}

/**
 * بررسی کد واردشده.
 * خروجی: { token } — این توکن باید همراه درخواست ساخت تصویر ارسال شود.
 */
export async function verifyTryOnOtp({ storeId, phone, code, signal }) {
  const out = await postJson(
    `${storeBase(storeId)}/try-on/otp/verify/`,
    { customer_mobile: phone, code },
    signal,
  );

  return { token: out.token || out.otp_token || "" };
}

/* ============================== TRY-ON ============================== */

/**
 * عکس کاربر را می‌فرستد و آدرس تصویر نهایی را برمی‌گرداند.
 * با signal می‌شود درخواست را لغو کرد.
 */
export async function runTryOn({
  storeId,
  productId,
  image, // Blob
  colorId = null,
  productImageUrl = "",
  signal,
  customerName,
  customerPhone,
  otpToken = "",
}) {
  const form = new FormData();
  form.append("person_image", image, "photo.jpg");
  form.append("customer_mobile", customerPhone);
  if (customerName) form.append("customer_name", customerName);
  if (otpToken) form.append("otp_token", otpToken);
  if (colorId) form.append("color_id", colorId);
  if (productImageUrl) form.append("product_image_url", productImageUrl);

  const first = await request(base(storeId, productId), {
    method: "POST",
    body: form,
    signal,
  });

  // حالت مستقیم
  const direct = first.result;
  if (direct) return direct;

  // حالت ناهمگام
  const taskId = first.task_id || first.id;
  if (!taskId) throw new Error("پاسخ نامعتبر از سرور دریافت شد.");

  const startedAt = Date.now();

  while (Date.now() - startedAt < TIMEOUT_MS) {
    await sleep(POLL_INTERVAL_MS, signal);

    const s = await request(`${base(storeId, productId)}${taskId}/`, {
      signal,
    });
    const status = String(s.status || "").toLowerCase();

    if (status === "done" || status === "completed" || status === "success") {
      const url = s.result || s.image;
      if (!url) throw new Error("تصویر نهایی در پاسخ سرور نبود.");
      return url;
    }

    if (status === "failed" || status === "error") {
      throw new Error(
        s.detail || "ساخت تصویر ناموفق بود. عکس دیگری امتحان کنید.",
      );
    }
  }

  throw new Error("پردازش بیش از حد طول کشید. لطفاً دوباره تلاش کنید.");
}

export async function runWebsiteTryOn({
  storeId,
  productId,
  image, // فایل عکس مشتری
  productImageUrl, // مسیر عکس محصول در public
  colorId = null,
  signal,
  customerName,
  customerPhone,
  otpToken = "",
}) {
  if (!image) {
    throw new Error("عکس کاربر انتخاب نشده است.");
  }

  if (!productImageUrl) {
    throw new Error("آدرس عکس محصول ارسال نشده است.");
  }

  const form = new FormData();

  // عکس مشتری
  form.append("person_image", image, "person.jpg");

  // دریافت عکس محصول از پوشه public
  const imageResponse = await fetch(productImageUrl, {
    signal,
    credentials: "same-origin",
  });

  if (!imageResponse.ok) {
    throw new Error("دریافت عکس محصول ناموفق بود.");
  }

  const productImageBlob = await imageResponse.blob();

  if (!productImageBlob.type.startsWith("image/")) {
    throw new Error("فایل انتخاب‌شده تصویر معتبر نیست.");
  }

  const pathname = new URL(productImageUrl, window.location.origin).pathname;

  const filename = pathname.split("/").filter(Boolean).pop() || "product.jpg";

  const productImageFile = new File([productImageBlob], filename, {
    type: productImageBlob.type,
  });

  // عکس محصول؛ بک‌اند این فیلد را از request.FILES می‌خواند
  form.append("product_image", productImageFile);

  form.append("customer_mobile", customerPhone);

  if (customerName) {
    form.append("customer_name", customerName);
  }

  if (otpToken) {
    form.append("otp_token", otpToken);
  }

  if (colorId) {
    form.append("color_id", colorId);
  }

  // ارسال درخواست ساخت تصویر
  const first = await request(`${storeBase(storeId)}/try-on/`, {
    method: "POST",
    body: form,
    signal,
  });

  // اگر تصویر مستقیماً برگشت
  if (first.result) {
    return first.result;
  }

  // اگر پردازش ناهمگام بود، وضعیت را بررسی کن
  const taskId = first.task_id || first.id;

  if (!taskId) {
    throw new Error("پاسخ نامعتبر از سرور دریافت شد.");
  }

  const startedAt = Date.now();

  while (Date.now() - startedAt < TIMEOUT_MS) {
    await sleep(POLL_INTERVAL_MS, signal);

    const statusResponse = await request(
      `${base(storeId, productId)}${taskId}/`,
      { signal },
    );

    const status = String(statusResponse.status || "").toLowerCase();

    if (["done", "completed", "success"].includes(status)) {
      const result = statusResponse.result || statusResponse.image;

      if (!result) {
        throw new Error("تصویر نهایی در پاسخ سرور نبود.");
      }

      return result;
    }

    if (["failed", "error"].includes(status)) {
      throw new Error(
        statusResponse.detail ||
          "ساخت تصویر ناموفق بود. عکس دیگری امتحان کنید.",
      );
    }
  }

  throw new Error("پردازش بیش از حد طول کشید. لطفاً دوباره تلاش کنید.");
}
